// Account detail page — Overview / Documents / Touchpoints / Activity.
//
// Account is BeneCRM's canonical client file cabinet. The four tabs
// claim the spaces brokers expect: Overview (header KPIs, plan
// years, notes), Documents (the canonical file cabinet, including
// nested ``/plan-years/{year-range}/`` folders), Touchpoints (claimed
// space, content lands when the touchpoints feature ships), and
// Activity (claimed space for upload / plan-year / workflow events).
//
// The ``?tab=`` and ``?folder=`` query params drive the live state so
// the PlanYear "Open plan-year folder" shortcut can deep-link
// straight to the right folder inside Documents.

import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Link, useLocation, useParams, useSearchParams,
} from "react-router-dom";

import { BlockNoteEditor } from "@/components/blocknote";
import { DocumentLibrary } from "@/components/documents";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import { AccountTouchpointsTab } from "@/features/touchpoints";

import { AccountActivityTab } from "./AccountActivityTab";
import { AccountContactsTab } from "./AccountContactsTab";
import { AccountDetailHeader } from "./AccountDetailHeader";
import { AccountClientTeamCard } from "./AccountClientTeamCard";
import { AccountOverviewCards } from "./AccountOverviewCards";
import { AccountPlanYearsPanel } from "./AccountPlanYearsPanel";
import { updateAccount } from "./api";
import { ACCOUNTS_QUERY_KEY, useAccountDetailQuery } from "./hooks";
import { AccountNotFoundError, type AccountDetail } from "./types";


// Tab order is product-fixed: Overview → Documents → Contacts →
// Touchpoints → Activity. Contacts sits next to Documents so the
// broker has both the "what" (file cabinet) and the "who" (people)
// at the front of the Account file before the chronological feeds.
type Tab =
  | "overview" | "documents" | "contacts"
  | "touchpoints" | "activity";


const VALID_TABS: Tab[] = [
  "overview", "documents", "contacts", "touchpoints", "activity",
];


export function AccountDetailPage() {
  const params = useParams();
  const accountId = params.accountId;
  const query = useAccountDetailQuery(accountId);
  const savedBanner = useSavedBanner();
  const [search, setSearch] = useSearchParams();
  const tab: Tab = useMemo(() => readTab(search.get("tab")), [search]);
  const folder = search.get("folder") ?? undefined;

  if (!accountId) {
    return <NotFoundView />;
  }

  if (query.isLoading) {
    return (
      <div data-testid="account-detail-page" className="flex flex-col gap-5">
        <DashboardLoadingState variant="card" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof AccountNotFoundError) {
      return <NotFoundView />;
    }
    return (
      <div data-testid="account-detail-page" className="flex flex-col gap-5">
        <DashboardErrorState
          title="Couldn't load this Account"
          description="Refresh to retry, or check the network connection."
          retry={() => query.refetch()}
        />
      </div>
    );
  }

  const account = query.data;
  if (!account) return <NotFoundView />;

  function selectTab(next: Tab) {
    const params = new URLSearchParams(search);
    params.set("tab", next);
    if (next !== "documents") params.delete("folder");
    setSearch(params, { replace: true });
  }

  return (
    <div data-testid="account-detail-page" className="flex flex-col gap-5">
      {savedBanner && (
        <div
          data-testid="account-detail-save-banner"
          role="status"
          className="rounded-md bg-sage-tint px-3 py-2 text-sm text-sage"
        >
          {savedBanner === "updated"
            ? "Account saved."
            : "Account created."}
        </div>
      )}
      <AccountDetailHeader account={account} />
      <Tabs currentTab={tab} onSelect={selectTab} />

      {tab === "overview" && <OverviewTab account={account} />}
      {tab === "documents" && (
        <DocumentLibrary
          host={{ hostKind: "account", accountId: account.account_id }}
          title="Account documents"
          initialFolder={folder ?? "/"}
        />
      )}
      {tab === "contacts" && (
        <AccountContactsTab accountId={account.account_id} />
      )}
      {tab === "touchpoints" && (
        <AccountTouchpointsTab accountId={account.account_id} />
      )}
      {tab === "activity" && (
        <AccountActivityTab accountId={account.account_id} />
      )}
    </div>
  );
}


function readTab(raw: string | null): Tab {
  if (raw && (VALID_TABS as string[]).includes(raw)) return raw as Tab;
  return "overview";
}


function Tabs({
  currentTab, onSelect,
}: {
  currentTab: Tab;
  onSelect: (next: Tab) => void;
}) {
  return (
    <nav
      data-testid="account-detail-tabs"
      role="tablist"
      className="flex gap-2 border-b border-border-subtle"
    >
      {VALID_TABS.map((t) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={currentTab === t}
          data-testid={`account-detail-tab-${t}`}
          onClick={() => onSelect(t)}
          className={[
            "px-3 py-1.5 text-xs font-medium",
            currentTab === t
              ? "border-b-2 border-teal text-text-primary"
              : "text-text-secondary hover:text-text-primary",
          ].join(" ")}
        >
          {labelFor(t)}
        </button>
      ))}
    </nav>
  );
}


function labelFor(t: Tab): string {
  if (t === "overview") return "Overview";
  if (t === "documents") return "Documents";
  if (t === "contacts") return "Contacts";
  if (t === "touchpoints") return "Touchpoints";
  return "Activity";
}


function OverviewTab({ account }: { account: AccountDetail }) {
  return (
    <div className="flex flex-col gap-5" data-testid="account-overview-tab">
      <AccountOverviewCards account={account} />
      <AccountClientTeamCard
        accountId={account.account_id}
        initialTeam={account.client_team}
      />
      <AccountPlanYearsPanel accountId={account.account_id} />
      {/* "View Account Commitments" lives on the page header
          (top right). Notes anchor at the bottom of Overview so
          the high-signal surfaces stay above the fold. */}
      <AccountNotesSection account={account} />
    </div>
  );
}


function AccountNotesSection({ account }: { account: AccountDetail }) {
  const queryClient = useQueryClient();
  return (
    <section
      data-testid="account-notes-section"
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <header>
        <h2 className="text-sm font-semibold text-text-primary">Notes</h2>
      </header>
      <BlockNoteEditor
        documentId={
          (account as AccountDetail & { notes_document_id?: string | null })
            .notes_document_id ?? null
        }
        documentKind="account_notes"
        fallbackText={account.notes ?? null}
        onCreated={async (documentId) => {
          await updateAccount(account.account_id, {
            notes_document_id: documentId,
          } as never);
          await queryClient.invalidateQueries({
            queryKey: [ACCOUNTS_QUERY_KEY, "detail", account.account_id],
          });
        }}
      />
    </section>
  );
}


function useSavedBanner(): "updated" | "created" | null {
  const location = useLocation();
  const initial = (location.state as { saved?: "updated" | "created" } | null)
    ?.saved ?? null;
  const [banner, setBanner] = useState<"updated" | "created" | null>(initial);
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);
  return banner;
}


function NotFoundView() {
  return (
    <div
      data-testid="account-detail-not-found"
      className="flex flex-col gap-5"
    >
      <DashboardEmptyState
        title="Account not found"
        description="It may have been archived, or the URL is wrong."
        action={
          <Link
            to="/dashboard/accounts"
            data-testid="account-detail-not-found-back"
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Back to Accounts
          </Link>
        }
      />
    </div>
  );
}
